import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowUpRight, ChevronLeft, ChevronRight, Filter as FilterIcon, Mail, Phone, Users as UsersIcon, FileSpreadsheet, ShieldCheck, Settings, Trash2, Workflow } from "lucide-react";
import { listActivity, activitySummary } from "../lib/api";
import { fmtDate } from "../lib/format";

const ICONS = {
  kontakthistorie_seed: ShieldCheck,
  kontakthistorie_neu: Mail,
  investor_phase_change: Workflow,
  investor_update: Settings,
  investor_sperre: ShieldCheck,
  investor_dokument_status: FileSpreadsheet,
  investor_datenraum: FileSpreadsheet,
  dokument_upload: FileSpreadsheet,
  dokument_delete: Trash2,
  bulk_update: UsersIcon,
  nda_reminder_sent: Mail,
};

const TYPE_LABELS = {
  kontakthistorie_seed: "Kontakt-Seed",
  kontakthistorie_neu: "Kontakt-Eintrag",
  investor_phase_change: "Phase geändert",
  investor_update: "Investor geändert",
  investor_sperre: "Sperre",
  investor_dokument_status: "Dokument-Status",
  investor_datenraum: "Datenraum",
  dokument_upload: "Dokument hochgeladen",
  dokument_delete: "Dokument entfernt",
  bulk_update: "Bulk-Update",
  nda_reminder_sent: "NDA-Erinnerung",
};

const fmtRel = (iso) => {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function Aktivitaeten() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 50 });
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const [akteur, setAkteur] = useState(params.get("akteur") || "");
  const [aktionTyp, setAktionTyp] = useState(params.get("aktion_typ") || "");
  const [page, setPage] = useState(Number(params.get("page") || 1));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        listActivity({ page, page_size: 50,
          akteur: akteur || undefined,
          aktion_typ: aktionTyp || undefined }),
        activitySummary(365),
      ]);
      setData(list);
      setSummary(sum);
    } finally { setLoading(false); }
  }, [page, akteur, aktionTyp]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const p = {};
    if (akteur) p.akteur = akteur;
    if (aktionTyp) p.aktion_typ = aktionTyp;
    if (page > 1) p.page = String(page);
    setParams(p, { replace: true });
  }, [akteur, aktionTyp, page, setParams]);

  const aktionTypen = summary?.by_aktion_typ ? Object.keys(summary.by_aktion_typ).sort() : [];
  const totalPages = Math.max(1, Math.ceil(data.total / data.page_size));

  return (
    <div data-testid="aktivitaeten-page" className="space-y-8">
      <section>
        <div className="pb-eyebrow">Audit</div>
        <h1 className="font-serif text-4xl text-navy mt-2 leading-tight">Aktivitäten</h1>
        <p className="mt-2 text-sm text-navy/65 font-sans">
          Vollständiges Audit-Log aller System- und User-Aktionen. {summary && (
            <>Gesamt: <strong className="text-navy">{summary.total}</strong> Einträge.</>
          )}
        </p>
      </section>

      {/* Filter */}
      <section className="border border-navy/15 p-4 bg-ledger/30">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="pb-label">Akteur</label>
            <select data-testid="filter-akteur" className="pb-input--full" value={akteur} onChange={(e) => { setPage(1); setAkteur(e.target.value); }}>
              <option value="">Alle</option>
              {["Heinrichs","Gehring","Kasper","Adldinger","System","BNP-Seed"].map((a) => <option key={a}>{a}</option>)}
            </select>
          </div>
          <div className="md:col-span-5">
            <label className="pb-label">Aktion</label>
            <select data-testid="filter-aktion" className="pb-input--full" value={aktionTyp} onChange={(e) => { setPage(1); setAktionTyp(e.target.value); }}>
              <option value="">Alle</option>
              {aktionTypen.map((a) => <option key={a} value={a}>{TYPE_LABELS[a] || a} ({summary.by_aktion_typ[a]})</option>)}
            </select>
          </div>
          <div className="md:col-span-3 flex justify-end">
            <button onClick={() => { setAkteur(""); setAktionTyp(""); setPage(1); }} className="text-xs uppercase tracking-widish text-navy/55 hover:text-navy font-sans inline-flex items-center gap-1.5">
              <FilterIcon size={12}/> Filter zurücksetzen
            </button>
          </div>
        </div>
      </section>

      {/* Liste */}
      <section>
        {loading ? (
          <div className="py-12 text-center text-navy/45 font-sans">Lade …</div>
        ) : (
          <table className="pb-table" data-testid="aktivitaeten-table">
            <thead>
              <tr>
                <th>Zeit</th>
                <th>Akteur</th>
                <th>Typ</th>
                <th>Ressource</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 ? (
                <tr><td colSpan={5} className="text-navy/45 text-center py-10">Keine Einträge gefunden.</td></tr>
              ) : data.items.map((a) => {
                const Icon = ICONS[a.aktion_typ] || Settings;
                return (
                  <tr key={a.id} data-testid={`activity-row-${a.id}`}>
                    <td className="text-navy/70 font-sans whitespace-nowrap">{fmtRel(a.timestamp)}</td>
                    <td><span className="text-[10px] uppercase tracking-widish px-2 py-0.5 bg-ledger border border-navy/20 text-navy/80 font-sans">{a.akteur}</span></td>
                    <td>
                      <span className="inline-flex items-center gap-1.5 text-sm font-sans text-navy">
                        <Icon size={13} className="text-navy/60" strokeWidth={1.7}/>
                        {TYPE_LABELS[a.aktion_typ] || a.aktion_typ}
                      </span>
                    </td>
                    <td>
                      {a.ressource_typ === "investor" && a.ressource_id ? (
                        <Link to={`/investoren?search=${encodeURIComponent(a.ressource_name || "")}`} className="text-navy hover:text-gold inline-flex items-center gap-1">
                          {a.ressource_name} <ArrowUpRight size={12}/>
                        </Link>
                      ) : (
                        <span className="text-navy/70">{a.ressource_name || a.ressource_typ}</span>
                      )}
                    </td>
                    <td className="text-navy/65 font-sans text-xs max-w-md truncate">
                      {Object.entries(a.details || {}).slice(0, 3).map(([k, v]) => (
                        <span key={k} className="mr-2"><strong>{k}:</strong> {typeof v === "object" ? JSON.stringify(v) : String(v)}</span>
                      ))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="flex items-center justify-between mt-4 text-sm font-sans text-navy/70">
          <div data-testid="aktivitaeten-pagination-info">
            {data.total === 0 ? "0 Treffer" : `${(data.page - 1) * data.page_size + 1}–${Math.min(data.total, data.page * data.page_size)} von ${data.total}`}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="pb-btn-ghost p-2 disabled:opacity-30">
              <ChevronLeft size={16}/>
            </button>
            <span>Seite {data.page} / {totalPages}</span>
            <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} className="pb-btn-ghost p-2 disabled:opacity-30">
              <ChevronRight size={16}/>
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
